export type AffordableUnitAssignmentMode = "holding" | "direct";

export const getAffordableUnitAssignmentMode = (
  property?: {
    affordableUnitAssignmentMode?: AffordableUnitAssignmentMode | null;
  } | null
): AffordableUnitAssignmentMode =>
  property?.affordableUnitAssignmentMode === "direct" ? "direct" : "holding";

export const isAffordableApplicationFlow = (
  _unit?: { number?: string | null } | null,
  property?: { affordableHousing?: boolean | null } | null
): boolean => property?.affordableHousing === true;

export const isAffordableHoldUnit = (
  unit?: { number?: string | null } | null,
  property?: { affordableHousing?: boolean | null } | null
): boolean => unit?.number === "HOLD" && property?.affordableHousing === true;

export const shouldRedirectDirectAffordableToAvailability = ({
  property,
  unitID,
  unitNumber,
  applicationId,
  personId,
  leaseId,
}: {
  property?: {
    affordableHousing?: boolean | null;
    affordableUnitAssignmentMode?: AffordableUnitAssignmentMode | null;
  } | null;
  unitID?: string | null;
  unitNumber?: string | null;
  applicationId?: string | null;
  personId?: string | null;
  leaseId?: string | null;
}): boolean =>
  property?.affordableHousing === true &&
  getAffordableUnitAssignmentMode(property) === "direct" &&
  !unitNumber &&
  !unitID &&
  !applicationId &&
  !personId &&
  !leaseId;

export const hasLeaseTermParam = (leaseTerm?: string | null): boolean => {
  if (leaseTerm === null || leaseTerm === undefined) return false;
  const normalized = String(leaseTerm).trim().toLowerCase();
  return (
    normalized !== "" && normalized !== "null" && normalized !== "undefined"
  );
};

export const getLeaseTermSearchParam = (
  searchParams: URLSearchParams
): string | null => searchParams.get("leaseTerm") ?? searchParams.get("term");

export const formatAvailabilityMoveInDate = (
  moveInDate?: string | null
): string | null => {
  if (!moveInDate) return null;
  const text = String(moveInDate).trim();
  if (!text) return null;

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (iso) {
    const [, year, month, day] = iso;
    const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) &&
      date.toISOString().slice(0, 10) === text
      ? text
      : null;
  }

  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (us) {
    const [, month, day, year] = us;
    const normalized = `${year}-${month.padStart(2, "0")}-${day.padStart(
      2,
      "0"
    )}`;
    const date = new Date(`${normalized}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) &&
      date.toISOString().slice(0, 10) === normalized
      ? normalized
      : null;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toISOString().slice(0, 10);
};

export const isUnitDateOutOfWindowError = (err: unknown): boolean => {
  const msg: unknown =
    (err as any)?.data?.message ??
    (err as any)?.data?.err ??
    (err as any)?.data ??
    (err as any)?.message ??
    "";
  return (
    typeof msg === "string" &&
    msg.toLowerCase().includes("exceeds the maximum days after availability")
  );
};

/**
 * Primary deep-links recover by picking a new move-in date. Co-applicant /
 * existing-lease invites already have a locked lease — never send them to
 * availability to re-shop the unit.
 */
export const shouldShowMoveInOutOfWindowRecovery = ({
  leaseId,
  error,
}: {
  leaseId?: string | null;
  error: unknown;
}): boolean => Boolean(error) && !leaseId && isUnitDateOutOfWindowError(error);

export const buildMissingLeaseTermAvailabilityPath = ({
  property,
  unitID,
  unitNumber,
  moveInDate,
  leaseTerm,
  applicationId,
  personId,
  leaseId,
}: {
  property?: { id?: string | null; abbreviation?: string | null } | null;
  unitID?: string | null;
  unitNumber?: string | null;
  moveInDate?: string | null;
  leaseTerm?: string | null;
  applicationId?: string | null;
  personId?: string | null;
  leaseId?: string | null;
}): string | null => {
  if (hasLeaseTermParam(leaseTerm) || applicationId || personId || leaseId) {
    return null;
  }

  const unitNumberForRoute = unitNumber || unitID;
  const propertyCode = property?.abbreviation || property?.id;
  const availabilityMoveInDate = formatAvailabilityMoveInDate(moveInDate);

  if (!unitNumberForRoute || !propertyCode || !availabilityMoveInDate) {
    return null;
  }

  const params = new URLSearchParams({
    moveInDate: availabilityMoveInDate,
    chooseLeaseTerm: "true",
  });

  return `/availability/${encodeURIComponent(
    propertyCode
  )}/units/${encodeURIComponent(unitNumberForRoute)}?${params.toString()}`;
};
